export type Article = Content;

export type Content = HasAdditionalText & HasThumbnail & {
    isFeatured?: boolean | undefined,
    pdfLink?: string | undefined,
    registrationUri?: string | undefined,
    webinarDate?: string | undefined
}

export type Author = {
    readonly id: string;
    readonly publishedAt: string;
    readonly slug: string;
    readonly name: string;
    readonly pictureUri: string;
    readonly biography: string;
    readonly shortBiography: string;
}

export type BonusReport = Content;

export enum BpiStatus {
    // 1 - Bull Correction: Chart is on a p&f buy signal but is falling (column of O's) without yet reaching 70%.
    BullCorrection = 'Bull Correction',
    // 2 - Bull Confirmed: Chart is on a p&f buy signal and is rising (column of X's); and/or is in a column of X's above 68%.
    BullConfirmed = 'Bull Confirmed',
    // 3 - Bull Alert: Chart rising (X's) moving up from below 30% but has not yet generated a p&f buy.
    BullAlert = 'Bull Alert',
    // 4 - Bear Alert: Chart is falling from above 70% to below 70% without yet generating a p&f sell.
    BearAlert = 'Bear Alert',
    // 5 - Bear Confirmed: Chart is falling (Column O's) below 70% and has generated a p&f sell.
    BearConfirmed = 'Bear Confirmed',
    // 6 - Bear Correction: Chart is on a p&f sell but is rising (column X's) without having moved above 68%.
    BearCorrection = 'Bear Correction',
    // 7 - Bull Top: The chart is falling (column of O's) but is still above 70%.
    BullTop = 'Bull Top'
}

export type BullishPercentIndex = {
    val: number,
    col: string,
    col_chg_dt?: string | null | undefined,
    sig: string,
    sig_chg_dt?: string | null | undefined,
    stat: string | null | undefined,
    stat_chg_dt?: string | null | undefined,
}

export type Category = {
    readonly id: string;
    readonly name: string;
    readonly slug: string;
}

export type ContentfulArticle = {
    id: string,
    publish_dt: string,
    publish_at?: string | undefined,
    publish_vs?: number | undefined,
    slug: string,
    title?: string | undefined,
    body?: string | undefined,
    body2?: string | undefined,
    video_id?: number | undefined,
    thumbnail_uri?: string | undefined,

    author_id: string,
    is_public: boolean,

    assoc_category_ids?: string[] | undefined,
    assoc_ind_sector_ids?: string[] | undefined,
    assoc_product_ids?: string[] | undefined,
    assoc_sector_ids?: string[] | undefined,
    assoc_ticker_ids?: string[] | undefined,

    ad_product_id?: string | undefined,
    editorial_ad?: string | undefined,
    editorial_ad2?: string | undefined,

    preview?: string | undefined
}

export type ContentfulBonusReport = {
    id: string,
    pub_dt: string,
    publish_at?: string | undefined,
    publish_vs?: number | undefined,
    slug: string,
    name?: string | undefined,
    pdf_uri?: string | undefined,
    thumbnail_uri?: string | undefined,

    assoc_cat_ids?: string[] | undefined,
    assoc_product_ids?: string[] | undefined
}

export type ContentfulItem = HasMetaData & {
    publishedDate: string,
    title?: string | undefined,
    categories: Category[],
    products: Product[]
}

/**
 * The ContentfulSector type defines the data associated with a TMI Sector that is held in Contentful
 */
export type ContentfulSector = Sector & {
    // Links back the Contentful sys.id id value used as references within the Contentful content models
    contentfulId: string,
    // Provides the expected UI-friendly reference to the sector
    slug: string;
}

export type ContentfulTradeAlert = {
    id: string,
    slug: string,

    published_date: string,
    publish_at?: string | undefined,
    publish_vs?: number | undefined,

    title?: string | undefined,
    body?: string | undefined,
    body2?: string | undefined,
    video_link?: string | undefined,
    is_featured: boolean,
    preview?: string | undefined,
    thumbnail?: string | undefined,

    author_id: string,

    assoc_category_ids?: string[] | undefined,
    assoc_ind_sector_ids?: string[] | undefined,
    assoc_product_ids?: string[] | undefined,
    assoc_sector_ids?: string[] | undefined,
    assoc_ticker_ids?: string[] | undefined,

    ad_product_id?: string | undefined,
    editorial_ad?: string | undefined,
}

export type ContentfulWebinar = {
    id: string,
    publish_dt: string,
    publish_at?: string | undefined,
    publish_vs?: number | undefined,
    slug: string,
    title?: string | undefined,
    body?: string | undefined,
    featured: boolean,
    upcoming: boolean,
    reg_link?: string | undefined,
    video?: string | undefined,

    author_id: string,

    ad_product_id?: string | undefined,
    editorial_ad?: string | undefined,

    assoc_category_ids?: string[] | undefined,
    assoc_ind_sector_ids?: string[] | undefined,
    assoc_product_ids?: string[] | undefined,
    assoc_sector_ids?: string[] | undefined,
    assoc_ticker_ids?: string[] | undefined,
}

export type Countable< T > = {
    readonly item: T,
    count: number
}

export type Course = {
    id: string,
    slug: string,
    title: string,
    body?: string | null,
    thumbnail_uri?: string | null,
    assoc_product_ids: string[],
    published_dt: string,
    sections: Section[]
}

export type EquallyWeightedIndex = {
    log_rtn: number,
    val: number,
    delta?: number | undefined,
    col: string,
    col_chg_dt?: string | null | undefined,
    sig: string,
    sig_chg_dt?: string | null | undefined
}

export type HasAdditionalText = HasAuthor & {
    body2?: string | undefined
}

export type HasAuthor = HasEditorialAds & {
    author: Author | undefined
}

export type HasEditorialAds = HasSectorContext & {
    editorialAd?: string | undefined,
    adProduct?: Product | undefined
}

export type HasSectorContext = HasTextPreview & {
    sectors: Sector[],
    tickers: SearchableTicker[]
}

export type HasMetaData = {
    // Identifiers
    contentfulId: string;
    slug: string,

    // Version and temporal data
    updatedDate: string | undefined,
}

export type HasTextPreview = HasVideoContent & {
    text: string | undefined,
    preview: string | undefined
}

export type HasThumbnail = {
    thumbnail: string | undefined
}

export type HasVideoContent = ContentfulItem & {
    videoLink: string | undefined
}

export type Lesson = {
    id: string,
    title: string,
    slug: string,
    sequence_number: number,
    vimeo_video_id: string | null,
    questions: Question[] | null
}

export type ModelPortfolioTradeAlertLink = {
    date: string,
    alert_slug: string,
    closing_alert: boolean
}

export interface Product {
    id: string,
    slug: string,
    name: string,
    pageTitle: string,
    subTitle: string | undefined,
    body: string | undefined,
    codes: string | undefined,
    favicon: string | undefined,
    logo: string | undefined,
    logo_dark: string | undefined,
    isDisplay: boolean,
    scheduleAppointmentLink: string | undefined,
    plans: SubscriptionPlan[]
}

export type MomentumBreadth = {
    val: number,
    col: string,
    col_chg_dt?: string | null | undefined,
    sig: string,
    sig_chg_dt?: string | null | undefined
}

export type Question = {
    id: string,
    question: string,
    correct_answer_number: number,
    answers: string[],
    clarification: string | null
}

export type RelativeStrength = {
    rnk: number,
    log_rtn: number,
    val: number,
    col: string,
    col_chg_dt?: string | null | undefined,
    sig: string,
    sig_chg_dt?: string | null | undefined
}

export type SearchableTicker = {
    readonly id: string;
    readonly name: string;
    readonly ticker: string;
    readonly isEtf: boolean;
}

export type Section = {
    id: string,
    title: string,
    sequence_number: number,
    lessons: Lesson[]
}

/**
 * A Sector defines the data associated with a TMI Sector
 */
export type Sector = {
    // Links back the TMI-specific sector id values used as references within the sh database
    id: number,
    // Display name for the sector: "Oil Services", "Telecom", etc.
    name: string,
    // The four character code used to provide a short reference to the sector
    symbol: string
}

// The type that defines the event generated by a significant event in a specific sector
export type SectorAlert = {
    id: string,
    dt: string,
    sector_id: number,
    typeId: number,
    typeName: string,
    bullish: boolean,
    bpi: BullishPercentIndex,
    p30: MomentumBreadth
    p10: MomentumBreadth
    rs: RelativeStrength,
    stocks: string[],
    etfs: string[]
}

export type SectorMarketState = {
    dt: string,
    sector: ContentfulSector,
    idx?: EquallyWeightedIndex | undefined,
    bpi: BullishPercentIndex,
    p30: MomentumBreadth,
    p10: MomentumBreadth,
    rs: RelativeStrength,
    alerts?: SectorAlert[]
}

export type SectorMarketStateContainer = {
    date: string,
    history: SectorMarketState[]
}

export type SectorMarketStateValues = {
    dt: string,
    idx?: EquallyWeightedIndex | undefined,
    bpi: BullishPercentIndex,
    p30: MomentumBreadth,
    p10: MomentumBreadth,
    rs: RelativeStrength,
    alerts?: SectorAlert[]
}

export type SimplePageContent = {
    readonly id: string;
    readonly title: string;
    readonly slug: string;
    readonly body: string;
}

export type SubscriptionPlan = {
    id: string,
    title: string,
    recommended: boolean,
    duration: string,
    numIss: string,
    freq: string,
    desc: string,
    orderUrl: string,
    topFeatures: string
}

export type Ticker = {
    readonly id: string;
    readonly company: string;
    readonly name: string;
    readonly slug: string;
    readonly is_searchable: boolean;
    readonly is_etf: boolean;
}

export type TradeAlert = Content;

export type Webinar = Content;

export const AdType = {
    Banner: 'banner',
    InPlace: 'in-place',
    Popup: 'popup',
    SideRight: 'side-right',
    SideLeft: 'side-left'
} as const;

export const SiteLocation = {
    Dashboard: 'dashboard',
    Article: 'article',
    MediaPage: 'media'
} as const;

export type SellSignal = {
    id: number,
    entry_date: string,
    entry_price: number,
    exit_date: string,
    exit_price: number,
    sec_metrics: SecurityMetrics
}

export type SecurityMetrics = {
    ticker: string,
    name: string,
    price: number,
    sect_name: string,
    bpi_dir: string,
    p30_sig: string,
    p30_dir: string
}

export type DynamicAd = {
    contentfulId: string,
    title: string,
    body: string,

    priority: number,
    imageUri: string,
    url: string,
    buttonText: string,

    displayProducts: Array< string >,
    includedProducts: Array< string >,
    excludedProducts: Array< string >,

    adTypes: Array< string >,
    siteLocations: Array< string >,
    displayDelaySec?: number
}

export type NormalizedContent = {
    contentfulId: string;
    slug: string;
    updatedDate: string | undefined;
    publishedDate: string;
    title?: string;
    text?: string;
    body2?: string;
    preview?: string;
    thumbnail?: string;
    videoLink?: string;
    editorialAd?: string;
    isFeatured?: boolean;
    pdfLink?: string;
    registrationUri?: string;
    webinarDate?: string;
    authorId?: string;
    adProductId?: string;
    categoryIds: string[];
    productIds: string[];
    sectorIds: string[];
    tickerIds: string[];
}

export type NormalizedSectorMarketState = Omit< SectorMarketState, 'sector' > & {
    sectorTmiId: number;
}

export type NormalizedPersistedSectorsState = {
    dt: string;
    sectors: NormalizedSectorMarketState[];
}